import {createContext, ReactNode, useCallback, useLayoutEffect, useState} from 'react';

export type SnackbarState = 'hidden' | 'fadingIn' | 'visible' | 'fadingOut';

const TRANSITION_DURATION_DEFAULT = 300;
const SNACKBAR_VISIBLE_DURATION = 10 * 1000; // 10 seconds

type SnackbarContextType = {
  showSnackbar: (options?: {
    transitionDuration?: number;
  }) => void;
  hideSnackbar: () => void;
  isShown: boolean;
  opacity: number;
  transitionDuration: number;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: () => {},
  hideSnackbar: () => {},
  isShown: false,
  opacity: 0,
  transitionDuration: TRANSITION_DURATION_DEFAULT,
});

const SnackbarProvider = ({children}: {children?: ReactNode}) => {
  const [isShown, setIsShown] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [snackbarState, setSnackbarState] = useState<SnackbarState>('hidden');
  const [currentTransitionDuration, setCurrentTransitionDuration] = useState<number>(TRANSITION_DURATION_DEFAULT);

  const showSnackbar = useCallback<SnackbarContextType['showSnackbar']>(options => {
    const {transitionDuration} = options || {};
    if (transitionDuration) {
      setCurrentTransitionDuration(transitionDuration);
    }
    setIsShown(true);
  }, []);

  const hideSnackbar = useCallback<SnackbarContextType['hideSnackbar']>(() => {
    setSnackbarState('fadingOut');
  }, []);

  const reset = useCallback(() => {
    setCurrentTransitionDuration(TRANSITION_DURATION_DEFAULT);
  }, []);

  useLayoutEffect(() => {
    let actionTimer: NodeJS.Timeout;

    if (isShown && snackbarState === 'hidden') {
      setSnackbarState('fadingIn');
    }

    if (isShown && snackbarState === 'fadingIn') {
      setOpacity(1);
      actionTimer = setTimeout(() => {
        setSnackbarState('visible');
      }, currentTransitionDuration);
    }

    if (isShown && snackbarState === 'visible') {
      actionTimer = setTimeout(() => {
        setSnackbarState('fadingOut');
      }, SNACKBAR_VISIBLE_DURATION);
    }

    if (isShown && snackbarState === 'fadingOut') {
      setOpacity(0);
      actionTimer = setTimeout(() => {
        setSnackbarState('hidden');
        setIsShown(false);
        reset();
      }, currentTransitionDuration);
    }

    return () => {
      clearTimeout(actionTimer);
    };
  }, [currentTransitionDuration, isShown, reset, snackbarState]);

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        hideSnackbar,
        isShown,
        opacity,
        transitionDuration: currentTransitionDuration,
      }}
    >
      {children}
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
